import {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {createStructuredSelector} from "reselect";
import {connect} from "react-redux";
import {FormattedMessage, injectIntl} from "react-intl";
import {compose} from "redux";
import AppContainer from "components/AppContainer/AppContainer";
import ForegroundBlob from "basicComponents/ForegroundBlob/ForegroundBlob";
import ModalHeader from "basicComponents/ForegroundBlobFakeModalHeader/ForegroundBlobFakeModalHeaderV2";
import {makeStyles} from "@material-ui/core/styles";
import {Grid, Typography} from "@material-ui/core";
import messages from "./messages";
import orderMessages from "../Order/messages";
import {makeSelectDentistApp, makeSelectInsurersApp,} from "containers/App/selectors";
import SingleSelectComponent from "components/SingleSelectComponent/SingleSelectComponent";
import {
  createDentistAction,
  createPatientAction,
  getDentistActionApp,
  getInsurersActionApp,
} from "containers/App/actions";
import CustomInputComponent from "components/CustomInputComponent/CustomInputComponent";
import GlobalMessages from "messages/messages";
import ActionButton from "basicComponents/ActionButton/ActionButton";
import ToggleButton from "components/ToggleButton/ToggleButton";
import { ROLES, hasRole } from "utils/userStorage";

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2),
  },
  form: {
    maxWidth: "700px",
    marginLeft: "auto",
    marginRight: "auto",
  },
}));

function NewPatientPage({
  insurers = [],
  dentists = [],
  onGetInsurers,
  onGetDentists,
  onCreateDentist,
  onAddPatient,
  history,
  location,
}) {
  const classes = useStyles({});

  const [patient, setPatient] = useState({
    name: (location.state && location.state.name) || "",
    internalRef: (location.state && location.state.internalRef) || "",
    careId: null,
    insuranceNumber: "",
    dentistId: null,
    homeShipping: false,
    address: {},
  });

  useEffect(
    () => {
      onGetInsurers();
      onGetDentists();
    },
    [onGetInsurers, onGetDentists],
  );

  const mappedDentists = useMemo(
    () =>
      dentists.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    [dentists],
  );

  const mappedInsurers = useMemo(
    () =>
      insurers.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    [insurers],
  );

  const onButtonClick = useCallback(
    () => {
      onAddPatient(patient, p => {
        if (location.state.goToTreatmentFlow) {
          history.push(`/treatment/edit/${p.id}`, {
            fromTreatmentFlow: true,
            searchedDate: history.location?.state?.searchedDate,
          });
        } else {
          history.push(`/patient/${p.id}`);
        }
      });
    },
    [onAddPatient, patient, history, location],
  );

  const handleChange = useCallback(
    property => e => {
      e.persist();
      setPatient(p => ({
        ...p,
        [property]: e.target.value,
      }));
    },
    [setPatient],
  );

  const handleAddressChange = useCallback(
    property => e => {
      e.persist();
      setPatient(p => ({
        ...p,
        address: {
          ...p.address,
          [property]: e.target.value,
        },
      }));
    },
    [setPatient],
  );

  const [errorName, setErrorName] = useState(false);

  const handleNameError = useCallback(
    e => {
      e.persist();
      if (e.target.value.length < 2) {
        setErrorName(true);
      } else {
        setErrorName(false);
      }
      setPatient(p => ({ ...p, name: e.target.value }));
    },
    [setPatient, setErrorName],
  );

  const [errorIns, setErrorIns] = useState(false);

  const handleInsuranceError = useCallback(
    e => {
      e.persist();
      if (e.target.value.length < 2) {
        setErrorIns(true);
      } else {
        setErrorIns(false);
      }
      setPatient(p => ({ ...p, insuranceNumber: e.target.value }));
    },
    [setPatient, setErrorIns],
  );

  return (
    <AppContainer>
      <ForegroundBlob noLargePadding noPadding fullscreen>
        {/* <div style={{background:'white'}}> */}
          <ModalHeader  onClose={() => history.push("/dashboard")}>
            <Typography variant="h5" data-cy="newPatientTitle">
              <FormattedMessage {...messages.new_patient_title} />
            </Typography>
          </ModalHeader>
        {/* </div> */}
        <div className={classes.content} style={{background:'white', borderRadius:'10px', border:"1px solid black", margin:"20px"}}>
          <form className={classes.form}  onSubmit={onButtonClick}>
            <Grid container spacing={2} >
              <Grid item xs={12} md={6} className={classes.header}>
                <div className={classes.components} data-cy="nameComponent">
                  <CustomInputComponent
                    fullWidth
                    label={<FormattedMessage {...GlobalMessages.patientId} />}
                    isError={errorName}
                    className={classes.input}
                    value={patient.name}
                    onChange={e => {
                      handleNameError(e);
                    }}
                    helperText="Minimal 2 Characters"
                    isRequired
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6} className={classes.header}>
                <div
                  className={classes.components}
                  data-cy="internalRefComponent"
                >
                  <CustomInputComponent
                    fullWidth
                    label={
                      <FormattedMessage {...GlobalMessages.internal_ref} />
                    }
                    className={classes.input}
                    value={patient.internalRef}
                    onChange={handleChange("internalRef")}
                  />
                </div>
              </Grid>
              {(() => {
                var isAllowed = hasRole([ROLES.Admin,ROLES.CS])
                if(isAllowed){
                  return <Grid item xs={12} md={6} className={classes.header}>
                    <div
                      className={classes.components}
                      data-cy="comments"
                    >
                      <CustomInputComponent
                        fullWidth
                        label={
                          <div>opmerking over patient</div>
                        }
                        className={classes.input}
                        value={patient.comments}
                        onChange={handleChange("comments")}
                      />
                    </div>
                  </Grid>
                }
              })()}
              
              <Grid item xs={12} md={6} className={classes.header}>
                <div className={classes.components} data-cy="insurerComponent">
                  <SingleSelectComponent
                    value={
                      mappedInsurers.find(i => +i.value === patient.careId) ||
                      {}
                    }
                    label={<FormattedMessage {...GlobalMessages.insurer} />}
                    noIcon
                    options={mappedInsurers}
                    onChange={({ value }) => {
                      setPatient(p => ({ ...p, careId: value }));
                    }}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6} className={classes.header}>
                <div
                  className={classes.components}
                  data-cy="insuranceNumberComponent"
                >
                  <CustomInputComponent
                    fullWidth
                    label={
                      <FormattedMessage {...GlobalMessages.insuranceNumber} />
                    }
                    isError={errorIns}
                    className={classes.input}
                    value={patient.insuranceNumber}
                    onChange={e => {
                      handleInsuranceError(e);
                    }}
                    helperText="Minimal 2 Characters"
                    isRequired
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6} className={classes.header}>
                <div className={classes.components} data-cy="dentistComponent">
                  <SingleSelectComponent
                    value={
                      mappedDentists.find(
                        i => +i.value === patient.dentistId,
                      ) || {}
                    }
                    label={<FormattedMessage {...GlobalMessages.dentist} />}
                    canCreate
                    noIcon
                    options={mappedDentists}
                    additionalAutocompleteOptions={{
                      clearOnBlur: false,
                      selectOnFocus: true,
                    }}
                    onChange={({ value, isNew = false }) => {
                      // Create new dentist
                      if (isNew) {
                        onCreateDentist({ name: value }, dentist => {
                          setPatient(p => ({ ...p, dentistId: dentist.id }));
                        });
                      } else {
                        setPatient(p => ({ ...p, dentistId: value }));
                      }
                    }}
                  />
                </div>
              </Grid>
              <Grid item xs={12} className={classes.header}>
                <div className={classes.components}>
                  <ToggleButton
                    onChange={({ value }) => {
                      setPatient(p => ({ ...p, homeShipping: value }));
                    }}
                    label={
                      <FormattedMessage {...orderMessages.homeTreatment} />
                    }
                    options={[
                      {
                        label: (
                          <FormattedMessage {...GlobalMessages.yesButtonText} />
                        ),
                        id: 1,
                        value: true,
                      },
                      {
                        label: (
                          <FormattedMessage {...GlobalMessages.noButtonText} />
                        ),
                        id: 2,
                        value: false,
                      },
                    ]}
                    value={patient.homeShipping}
                  />
                </div>
              </Grid>
              {patient.homeShipping && (
                <>
                  <Grid item xs={12} className={classes.header}>
                    <div className={classes.components}>
                      <CustomInputComponent
                        fullWidth
                        label={<FormattedMessage {...GlobalMessages.tel_nr} />}
                        className={classes.input}
                        value={(patient && patient.address.telephone_nr) || ""}
                        onChange={handleAddressChange("telephone_nr")}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={4} className={classes.header}>
                    <div className={classes.components}>
                      <CustomInputComponent
                        fullWidth
                        label={
                          <FormattedMessage {...GlobalMessages.streetNoColon} />
                        }
                        className={classes.input}
                        value={(patient && patient.address.street) || ""}
                        onChange={handleAddressChange("street")}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={4} className={classes.header}>
                    <div className={classes.components}>
                      <CustomInputComponent
                        fullWidth
                        label={
                          <FormattedMessage {...GlobalMessages.house_nr} />
                        }
                        className={classes.input}
                        value={(patient && patient.address.house_nr) || ""}
                        onChange={handleAddressChange("house_nr")}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={4} className={classes.header}>
                    <div className={classes.components}>
                      <CustomInputComponent
                        fullWidth
                        label={
                          <FormattedMessage
                            {...GlobalMessages.houseNrAddendum}
                          />
                        }
                        className={classes.input}
                        value={
                          (patient && patient.address.house_nr_addendum) || ""
                        }
                        onChange={handleAddressChange("house_nr_addendum")}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6} className={classes.header}>
                    <div className={classes.components}>
                      <CustomInputComponent
                        fullWidth
                        label={
                          <FormattedMessage
                            {...GlobalMessages.zipcodeNoColon}
                          />
                        }
                        className={classes.input}
                        value={(patient && patient.address.zipcode) || ""}
                        onChange={handleAddressChange("zipcode")}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6} className={classes.header}>
                    <div className={classes.components}>
                      <CustomInputComponent
                        fullWidth
                        label={
                          <FormattedMessage {...GlobalMessages.cityNoColon} />
                        }
                        className={classes.input}
                        value={(patient && patient.address.city) || ""}
                        onChange={handleAddressChange("city")}
                      />
                    </div>
                  </Grid>


                  <Grid item xs={12} md={12} className={classes.header}>
                    <div className={classes.components}>
                      <CustomInputComponent
                        multiline
                        fullWidth
                        label={
                          <div>opmerking over thuisbehandeling</div>
                        }
                        className={classes.input}
                        value={(patient && patient.address.comments) || ""}
                        onChange={handleAddressChange("comments")}
                      />
                    </div>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <ActionButton
                  fullWidth
                  disabled={
                    errorName ||
                    patient.name.length < 2 ||
                    errorIns ||
                    patient.insuranceNumber.length < 2 ||
                    patient.careId === null ||
                    patient.dentistId === null
                  }
                  onClick={onButtonClick}
                  dataCy="saveButton"
                >
                  <FormattedMessage {...GlobalMessages.save} />
                </ActionButton>
              </Grid>
            </Grid>
          </form>
        </div>
      </ForegroundBlob>
    </AppContainer>
  );
}

NewPatientPage.propTypes = {
  insurers: PropTypes.array,
  dentists: PropTypes.array,
  onGetDentists: PropTypes.func.isRequired,
  onGetInsurers: PropTypes.func.isRequired,
  onAddPatient: PropTypes.func.isRequired,
  onCreateDentist: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  insurers: makeSelectInsurersApp(),
  dentists: makeSelectDentistApp(),
});

function mapDispatchToProps(dispatch) {
  return {
    onGetDentists: () => dispatch(getDentistActionApp()),
    onGetInsurers: () => dispatch(getInsurersActionApp()),
    onCreateDentist: (dentist, cb) =>
      dispatch(createDentistAction(dentist, cb)),
    onAddPatient: (patient, cb) => dispatch(createPatientAction(patient, cb)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default injectIntl(compose(withConnect)(NewPatientPage));
